import { useState, useEffect } from "react";
import React from "react";
import chroma from "chroma-js";
import Grid from "@mui/material/Grid2";
import Slider, { SliderThumb } from "@mui/material/Slider";
import { Divider, Paper, Typography } from "@mui/material";
import Ohtani from "./images/Ohtani.png";
import Strider from "./images/Strider.png";

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

const thumbscale = chroma.scale(["#3661AD", "#C2C2C2", "#D82129"]);

interface SplitBarProps {
  percentile: number;
  percentile_raw: number;
  mirrored?: boolean;
  isDesktop: boolean;
}

function SplitBar({ percentile, percentile_raw, mirrored = false, isDesktop }: SplitBarProps) {
  interface ThumbComponentProps extends React.HTMLAttributes<unknown> {
    value: number;
  }

  function ThumbComponent(props: ThumbComponentProps) {
    const { children, ...other } = props;
    return (
      <SliderThumb {...other}>
        {children}
        <p
          style={{
            color: "white",
            fontWeight: "bold",
            fontSize: "0.8em",
            margin: 0,
            transform: mirrored ? "scaleX(-1)" : undefined,
          }}
        >
          {Number.isNaN(percentile)
            ? "NQ"
            : percentile_raw > 0.99 && percentile_raw < 1
            ? 99
            : Math.round(percentile)}
        </p>
      </SliderThumb>
    );
  }

  const color = thumbscale(isNaN(percentile_raw) ? 0.5 : percentile_raw).toString();

  return (
    <div style={{ transform: mirrored ? "scaleX(-1)" : undefined, padding: isDesktop ? "0" : "0 8px" }}>
      <Slider
        slotProps={{
          thumb: { value: percentile } as ThumbComponentProps,
        }}
        slots={{ thumb: ThumbComponent }}
        value={isNaN(percentile) ? 50 : percentile}
        step={10}
        marks
        min={0}
        max={100}
        disabled
        sx={{
          "& .MuiSlider-thumb": {
            backgroundColor: "var(--thumb)",
            outline: "2px solid white",
            ...(isDesktop ? {} : {
              width: 26,
              height: 18,
              borderRadius: "50%",
            }),
          },
          "& .MuiSlider-track": {
            backgroundColor: "var(--track)",
            height: isDesktop ? 18 : 12,
            borderRadius: 0,
          },
          "& .MuiSlider-rail": {
            backgroundColor: "var(--rail)",
            height: isDesktop ? 6 : 4,
            borderRadius: 0,
          },
          "& .MuiSlider-active": {
            backgroundColor: "var(--active)",
            borderRadius: 0,
          },
        }}
        style={
          {
            "--thumb": color,
            "--track": color,
            "--rail": "#C7DCDC",
            "--active": color,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

interface SplitRowProps {
  stat: string;
  vrValue: string;
  vlValue: string;
  vrPct: number;
  vlPct: number;
  isDesktop: boolean;
}

function SplitRow({ stat, vrValue, vlValue, vrPct, vlPct, isDesktop }: SplitRowProps) {
  return (
    <div>
      <Grid
        container
        columns={20}
        alignItems="center"
        sx={
          isDesktop
            ? { marginTop: "-1px", marginBottom: "-1px" }
            : { marginTop: "-7px", marginBottom: "-7px" }
        }
      >
        <Grid size={isDesktop ? 2 : 3}>
          <Typography variant="body2" textAlign="right" fontSize="0.72rem" sx={{ paddingRight: "6px" }}>
            {vlValue}
          </Typography>
        </Grid>
        <Grid size={isDesktop ? 6 : 5}>
          <SplitBar percentile={vlPct * 100} percentile_raw={vlPct} mirrored isDesktop={isDesktop} />
        </Grid>
        <Grid size={4}>
          <Typography
            variant="body2"
            textAlign="center"
            fontWeight={500}
            fontSize="0.72rem"
            sx={{ lineHeight: 1.2 }}
          >
            {stat}
          </Typography>
        </Grid>
        <Grid size={isDesktop ? 6 : 5}>
          <SplitBar percentile={vrPct * 100} percentile_raw={vrPct} isDesktop={isDesktop} />
        </Grid>
        <Grid size={isDesktop ? 2 : 3}>
          <Typography variant="body2" textAlign="left" fontSize="0.72rem" sx={{ paddingLeft: "6px" }}>
            {vrValue}
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
}

export default function SplitsPercentiles(props: any) {
  const [splitData, setSplitData] = useState<{ vr: any; vl: any } | null>(null);

  useEffect(() => {
    if (!props.id || !props.position || !props.level || !props.season) return;
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(
      `${base}/get_player_splits/${props.id}/${props.position}/${props.level}/${props.season}/${props.qualifier}/${props.ageRange[0]}/${props.ageRange[1]}`
    )
      .then((res) => res.json())
      .then((data) => setSplitData(data))
      .catch(() => setSplitData({ vr: {}, vl: {} }));
  }, [props.id, props.position, props.level, props.season, props.qualifier, props.ageRange, props.prod]);

  if (
    !splitData ||
    (Object.keys(splitData.vr || {}).length === 0 &&
      Object.keys(splitData.vl || {}).length === 0)
  ) {
    return null;
  }

  const vr = splitData.vr || {};
  const vl = splitData.vl || {};
  const isP = props.position === "P";

  const abbName =
    props.playerInfoProp?.common?.teamInfo?.MLB_AbbName ?? props.abbName;
  const bgColor =
    abbName && teamColors[abbName] ? teamColors[abbName][0] : teamColors["FA"][0];
  const textColor =
    abbName && teamColors[abbName] ? teamColors[abbName][1] : teamColors["FA"][1];

  const fmt3 = (v: any) => {
    const n = Number(v);
    return isNaN(n) || n === 0 ? "-" : n.toFixed(3);
  };
  const fmtPct = (v: any) => {
    const n = Number(v);
    return isNaN(n) || n === 0 ? "-" : n.toFixed(1) + "%";
  };
  const fmtMph = (v: any) => {
    const n = Number(v);
    return isNaN(n) || n === 0 ? "-" : n.toFixed(1) + (props.isDesktop ? " mph" : "");
  };

  const p = (v: any) => (isNaN(Number(v)) ? 0.5 : Number(v));

  const leftLabel = isP ? "← vs LHB" : "← vs LHP";
  const rightLabel = isP ? "vs RHB →" : "vs RHP →";

  // Data-driven stat rows. A row is shown only when at least one handedness
  // split has a real value for it. This lets the aggregate levels (AA / A+, the
  // Cal/Carolina Single-A leagues and Rookie ball) -- which only have zone
  // discipline, no x-stats / EV -- render just their supported splits instead
  // of a wall of blank "-" rows.
  type RowDef = { stat: string; key: string; fmt: (v: any) => string };
  const ROWS: RowDef[] = [
    { stat: "xwOBA", key: "xwoba", fmt: fmt3 },
    { stat: "xBA", key: "xba", fmt: fmt3 },
    { stat: "xSLG", key: "xslg", fmt: fmt3 },
    { stat: "EV", key: "ev", fmt: fmtMph },
    { stat: "Barrel%", key: "barrelbbe", fmt: fmtPct },
    { stat: "HH%", key: "hhrate", fmt: fmtPct },
    { stat: "K%", key: "krate", fmt: fmtPct },
    { stat: "BB%", key: "bbrate", fmt: fmtPct },
    { stat: "Chase%", key: "chaserate", fmt: fmtPct },
    { stat: "Whiff%", key: "whiffrate", fmt: fmtPct },
    { stat: "Swing%", key: "swing", fmt: fmtPct },
    { stat: "Z-Swg%", key: "zswing", fmt: fmtPct },
    { stat: "Z-Con%", key: "zcontact", fmt: fmtPct },
    { stat: "SwStr%", key: "swstr", fmt: fmtPct },
  ];
  const hasVal = (v: any) => {
    const n = Number(v);
    return !isNaN(n) && n !== 0;
  };
  const visibleRows = ROWS.filter((r) => hasVal(vl[r.key]) || hasVal(vr[r.key]));

  return (
    <Paper
      sx={{
        maxWidth: "90vw",
        height: "100%",
        width: props.isDesktop ? 750 : 500,
        border: "#293241 solid 2px",
        boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
      }}
      square
      variant="outlined"
    >
      {/* Header */}
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        style={{
          backgroundColor: bgColor,
          paddingLeft: "10px",
          paddingBottom: "10px",
          marginBottom: "6px",
          wordWrap: "break-word",
        }}
      >
        <img
          src={isP ? Strider : Ohtani}
          alt=""
          style={{
            marginLeft: "10px",
            marginTop: "10px",
            width: props.isDesktop ? "64px" : "36px",
            maxWidth: "12vw",
          }}
        />
        <div style={{ marginLeft: "10px", marginTop: "10px", display: "flex", flexDirection: "column" }}>
          {(props.playerInfoProp?.common?.playerInfo?.firstLastName ?? props.playerName) && (
            <span
              style={{
                fontWeight: "bold",
                fontSize: props.isDesktop ? "13pt" : "10pt",
                color: textColor,
                lineHeight: 1.2,
              }}
            >
              {props.playerInfoProp?.common?.playerInfo?.firstLastName ?? props.playerName}
            </span>
          )}
          <span
            style={{
              fontWeight: "bold",
              fontSize: props.isDesktop ? "18pt" : "13pt",
              color: textColor,
              lineHeight: 1.2,
            }}
          >
            {isP ? "Pitcher" : "Hitter"} Splits
          </span>
        </div>
      </Grid>

      {/* Split column labels */}
      <Grid container columns={20} alignItems="center" sx={{ paddingBottom: "2px" }}>
        <Grid size={props.isDesktop ? 2 : 3} />
        <Grid size={props.isDesktop ? 6 : 5}>
          <Typography
            variant="caption"
            textAlign="center"
            display="block"
            color="text.secondary"
            fontWeight={600}
          >
            {leftLabel}
          </Typography>
        </Grid>
        <Grid size={4} />
        <Grid size={props.isDesktop ? 6 : 5}>
          <Typography
            variant="caption"
            textAlign="center"
            display="block"
            color="text.secondary"
            fontWeight={600}
          >
            {rightLabel}
          </Typography>
        </Grid>
        <Grid size={props.isDesktop ? 2 : 3} />
      </Grid>

      <Divider variant="middle" />

      {/* Stat rows (only those with data at this level) */}
      {visibleRows.map((r, i) => (
        <React.Fragment key={r.key}>
          <SplitRow
            stat={r.stat}
            vlValue={r.fmt(vl[r.key])}
            vrValue={r.fmt(vr[r.key])}
            vlPct={p(vl[`${r.key}_p`])}
            vrPct={p(vr[`${r.key}_p`])}
            isDesktop={props.isDesktop}
          />
          {i < visibleRows.length - 1 && <Divider variant="middle" />}
        </React.Fragment>
      ))}
    </Paper>
  );
}
